import { useEffect, useState } from "react";
import { Check, Copy, Phone } from "lucide-react";
import { FadeUp, Eyebrow, MaskedLines } from "./Reveal";
import QuoteForm from "./QuoteForm";

const EMAIL = "CFDigiCo@gmail.com";

export default function Contact() {
  const [copied, setCopied] = useState(false);
  const [time, setTime] = useState("");

  useEffect(() => {
    const tick = () =>
      setTime(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* clipboard unavailable */
    }
  };

  return (
    <>
      <section id="contact" className="relative overflow-hidden border-t border-white/[0.06] py-28 sm:py-36">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(212,175,55,0.08),transparent_60%)]" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Eyebrow className="mb-8">05 — Contact</Eyebrow>
              <MaskedLines
                inView
                as="h2"
                className="font-display text-4xl font-light leading-[1.02] tracking-tight sm:text-5xl"
                lines={[
                  { content: "Get a" },
                  { content: <span className="gold-text italic">free quote.</span> },
                ]}
              />
              <FadeUp delay={0.2}>
                <p className="mt-6 max-w-sm text-sm leading-relaxed text-neutral-400">
                  Tell me about your business and what you need — I'll come back
                  within 24 hours with next steps and a straight answer on cost.
                </p>
              </FadeUp>
              <FadeUp delay={0.3} className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  data-testid="contact-call-button"
                  href="tel:+61492884214"
                  className="flex items-center gap-2 rounded-full border border-[#D4AF37] bg-[#D4AF37] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-black transition-[background-color,box-shadow] duration-300 hover:bg-[#E5C158] hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]"
                >
                  <Phone className="h-3.5 w-3.5" />
                  Call 0492 884 214
                </a>
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${EMAIL}`}
                  className="group relative font-mono text-sm tracking-tight text-white transition-colors duration-300 hover:text-[#E5C158]"
                >
                  {EMAIL}
                  <span className="absolute -bottom-1.5 left-0 h-px w-full bg-white/20">
                    <span className="absolute inset-0 w-0 bg-[#D4AF37] transition-[width] duration-500 group-hover:w-full" />
                  </span>
                </a>
                <button
                  data-testid="contact-copy-email"
                  onClick={copyEmail}
                  className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-[border-color,color,background-color] duration-300 ${
                    copied
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#E5C158]"
                      : "border-white/15 text-neutral-400 hover:border-[#D4AF37]/60 hover:text-[#E5C158]"
                  }`}
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </FadeUp>
            </div>
            <div className="lg:col-span-7">
              <FadeUp delay={0.25}>
                <QuoteForm />
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/[0.06] bg-[#0A0A0C]">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 py-10 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 sm:px-10 lg:px-16 lg:flex-row">
          <p data-testid="footer-trademark">© {new Date().getFullYear()} CFDigital Co. All rights reserved.</p>
          <p data-testid="footer-status" className="flex items-center gap-2.5 text-[#E5C158]">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#D4AF37] opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#D4AF37]" />
            </span>
            Open for new projects
          </p>
          <p data-testid="footer-clock">Local — {time}</p>
        </div>
      </footer>
    </>
  );
}